export type ReelStatus = 'published' | 'unpublished';

export const REEL_STATUSES: readonly ReelStatus[] = ['published', 'unpublished'];

/** Canvas size guard rails (pixels) — mirrors `ReelSchema` on st-ck-server. */
export const REEL_MIN_DIMENSION = 16;
export const REEL_MAX_DIMENSION = 16384;

/** `BOS-Reel` document as returned by `/bos/reels`. */
export interface Reel {
    _id: string;
    companyId: string;
    /** Creator user id. */
    userId: string;
    updatedBy: string;
    name: string;
    description: string;
    /** Canvas width in pixels. */
    width: number;
    /** Canvas height in pixels. */
    height: number;
    status: ReelStatus;
    publishedAt: string | null;
    /** Working copy of the canvas; `null` until the canvas editor saves something. */
    canvas: unknown | null;
    /** Last published version number (0 = never published). */
    publishedVersion: number;
    /** Version the working copy becomes on publish, or `null` when there is no draft. */
    draftVersion: number | null;
    hasDraft: boolean;
    nextVersion: number;
    /** `v3-draft` while editing, else `v2` (last published). */
    versionLabel: string;
    /** `v2` or `null` when never published. */
    publishedLabel: string | null;
    /** Most recent video export of any published version. */
    lastExport?: ReelLastExport | null;
    createdAt: string;
    updatedAt: string;
}

export type ReelExportStatus = 'queued' | 'in-progress' | 'completed' | 'failed' | 'missing' | 'none' | '';

/** Denormalised export badge on a reel. */
export interface ReelLastExport {
    version: number;
    versionLabel: string;
    status: ReelExportStatus;
    jobId: string;
    worker?: string;
    error?: string;
    /** 0–100 while a worker is generating the video. Absent until progress is reported. */
    percent?: number | null;
    stage?: string;
    /** Short-lived URL of the finished MP4, for the tile thumbnail and muted hover playback. */
    previewUrl?: string | null;
    updatedAt?: string | null;
}

/** Immutable published snapshot (`BOS-ReelVersion`). `canvas` is present only on the single-version endpoint. */
export interface ReelVersion {
    _id: string;
    reelId: string;
    companyId: string;
    version: number;
    label: string;
    name: string;
    description: string;
    width: number;
    height: number;
    canvas?: unknown | null;
    publishedAt: string;
    publishedBy: string;
    export?: {
        status?: ReelExportStatus;
        jobId?: string;
        worker?: string;
        error?: string;
        s3Uri?: string;
        fileName?: string;
        sizeBytes?: number;
    } | null;
    createdAt: string;
    updatedAt: string;
}

/** `GET /bos/reels/:id/versions/:version/export` */
export interface ReelVersionExportStatus {
    status: ReelExportStatus;
    worker: string;
    progress: { stage: string; percent: number; message?: string; worker: string; updatedAt: number } | null;
    s3Uri: string;
    fileName: string;
    sizeBytes: number;
    error: string;
    available: boolean;
    downloadUrl?: string;
}

export interface ReelVersionsResponse {
    reelId: string;
    publishedVersion: number;
    draft: { version: number; label: string; updatedAt: string } | null;
    versions: ReelVersion[];
}

/** Payload of `GET /bos/reels/:id/versions/:version` (published snapshot or the working draft). */
export interface ReelVersionSnapshot {
    reelId: string;
    version: number;
    label: string;
    isDraft: boolean;
    name: string;
    description: string;
    width: number;
    height: number;
    canvas: unknown | null;
    publishedAt: string | null;
    updatedAt?: string;
}

/** Derived labels for reels loaded from older API responses without version fields. */
export const reelVersionLabel = (reel: Pick<Reel, 'publishedVersion' | 'draftVersion' | 'versionLabel'>): string =>
    reel.versionLabel ?? (reel.draftVersion != null ? `v${reel.draftVersion}-draft` : reel.publishedVersion > 0 ? `v${reel.publishedVersion}` : 'v1-draft');

export interface ReelUpsertPayload {
    name: string;
    description?: string;
    width: number;
    height: number;
}

export type ReelSortField = 'name' | 'createdAt' | 'updatedAt' | 'status' | 'width' | 'height';
export type ReelSortOrder = 'asc' | 'desc';
export type ReelStatusFilter = ReelStatus | 'all';
export type ReelViewMode = 'tile' | 'table';

export interface ListReelsQuery {
    status?: ReelStatusFilter;
    search?: string;
    sortBy?: ReelSortField;
    sortOrder?: ReelSortOrder;
}

/** Route helpers so the list, editor, breadcrumbs and menu agree on one URL shape. */
export const REEL_MAKER_PATH = '/beamonscreen/reel-maker';
export const reelEditorPath = (reelId: string) => `${REEL_MAKER_PATH}/${encodeURIComponent(reelId)}`;

/** Common presets offered in the New Reel dialog (portrait / landscape signage sizes). */
export const REEL_SIZE_PRESETS: ReadonlyArray<{ label: string; width: number; height: number }> = [
    { label: 'Full HD landscape (1920 × 1080)', width: 1920, height: 1080 },
    { label: 'Full HD portrait (1080 × 1920)', width: 1080, height: 1920 },
    { label: '4K UHD landscape (3840 × 2160)', width: 3840, height: 2160 },
    { label: '4K UHD portrait (2160 × 3840)', width: 2160, height: 3840 },
    { label: 'Square (1080 × 1080)', width: 1080, height: 1080 },
];

/** Response of `POST /bos/reels/:id/versions/vN/export`. */
export interface ReelExportQueued {
    jobId: string;
    reelId: string;
    version: number;
    versionLabel: string;
    fileName: string;
    s3Uri: string;
    /** Human readable destination, e.g. `Asset Explorer / Export`. */
    location: string;
    message: string;
}

/** Item of the Redis reel export queue (as listed by the server). */
export interface ReelExportQueueItem {
    jobId: string;
    status: 'in-progress' | 'scheduled';
    owner: string | null;
    visibleAt: number;
    enqueuedAt: number;
    enqueuedOrder: number;
    reelId: string;
    reelName: string;
    versionLabel: string;
    width: number;
    height: number;
    durationMs: number;
    companyId: string;
    companyName: string;
    requestedBy: string;
    s3Uri: string;
    outputKey: string;
    locationLabel: string;
    progress: { stage: string; percent: number; message?: string; worker: string; updatedAt: number } | null;
}
